import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  rectIntersection,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import Box from '@mui/material/Box';
import React, { PropsWithChildren, useState } from 'react';

import { themeColors } from '@/config/theme/themeColors.ts';
import { useAppConfig } from '@/contexts/appConfig/AppConfig.context.ts';
import { useEmailsContext } from '@/views/emails/context/EmailsContext.ts';
import {
  draggableThreadId,
  emailFolderDroppableId,
} from '@/views/emails/model/draggableEmailsIds.model.ts';
import { useMoveEmailsToFolder } from '@/views/emails/util/useMoveEmailsToFolder.util.ts';

const fixCursorSnapOffset: CollisionDetection = args => {
  // Bail out if keyboard activated
  if (!args.pointerCoordinates) {
    return rectIntersection(args);
  }
  const { x, y } = args.pointerCoordinates;
  const { height, width } = args.collisionRect;
  const updated = {
    ...args,
    // The collision rectangle is broken when using snapCenterToCursor. Reset
    // the collision rectangle based on pointer location and overlay size.
    collisionRect: {
      bottom: y + height / 2,
      height,
      left: x - width / 2,
      right: x + width / 2,
      top: y - height / 2,
      width,
    },
  };
  return rectIntersection(updated);
};

export const DraggableContext: React.FC<PropsWithChildren> = props => {
  const { isEmailEnabled } = useAppConfig();
  const { moveEmailsToFolder } = useMoveEmailsToFolder();
  const { emailsState } = useEmailsContext();

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const sensors = useSensors(mouseSensor);

  if (!isEmailEnabled) {
    return <>{props.children}</>;
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    void (async () => {
      if (event.over === null) {
        setActiveId(null);
        return;
      }
      if (!emailsState.selectedBox?.folderId) {
        throw new Error('No folder selected');
      }
      await moveEmailsToFolder({
        emailThreadId: event.active.id.toString().substring(draggableThreadId.length),
        sourceUserEmailFolderId: emailsState.selectedBox.folderId,
        targetUserEmailFolderId: event.over.id.toString().substring(emailFolderDroppableId.length),
      });
      setActiveId(null);
    })();
  };

  return (
    <DndContext
      collisionDetection={fixCursorSnapOffset}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      {props.children}
      <DragOverlay modifiers={[snapCenterToCursor]}>
        {activeId ? (
          <Box
            sx={{
              background: themeColors.stroke60,
              height: 60,
              width: 60,
            }}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
